import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserContext } from '../../contexts';
import { Analytics, createStripePaymentMethodUpdateLink, retryStripePastDuePayment } from '../../lib/api';
import { getMembershipLevelLabel } from '../../lib/subscriptionCapabilities';
import { getReadableDate } from '../../lib/utils';
import { Modal } from '../Modal';
import './PastDuePaymentModal.scss';

const isPastDue = (subscription) => (
    subscription?.is_past_due === true
    || subscription?.provider_status === 'past_due'
);

const hasRestrictedPaymentAccess = (subscription) => {
    if (subscription?.payment_access_restricted === true) {
        return true;
    }

    const deadline = subscription?.grace_days_ended_at;
    const deadlineTimestamp = deadline ? new Date(deadline).getTime() : null;

    return Number.isFinite(deadlineTimestamp) && deadlineTimestamp <= Date.now();
};

const getPastDuePlanName = (plan) => {
    const membershipLevel = getMembershipLevelLabel(plan);

    if (membershipLevel !== 'Free' && membershipLevel !== 'Skills') {
        return membershipLevel === 'PRO' ? 'Pro' : membershipLevel;
    }

    const configuredPlanName = String(plan?.product?.name ?? plan?.name ?? '').trim();
    return configuredPlanName || (membershipLevel === 'Skills' ? 'Skills' : 'Pro');
};

const PastDuePaymentModal = () => {
    const { user, isAuthenticating } = useUserContext();
    const [dismissed, setDismissed] = useState(false);
    const [openingPaymentUpdate, setOpeningPaymentUpdate] = useState(false);
    const [paymentRetryStatus, setPaymentRetryStatus] = useState(null);
    const [error, setError] = useState(null);
    const hasRetriedPayment = useRef(false);
    const hasTrackedModalShown = useRef(false);
    const subscription = user?.team?.subscription;
    const hasUser = Boolean(user);
    const isTeamOwner = user?.is_team_owner === true;
    const paymentPastDue = subscription?.stripe === true && isPastDue(subscription);
    const paymentAccessRestricted = hasRestrictedPaymentAccess(subscription);
    const returnedFromPaymentMethodUpdate = typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('billing') === 'payment-method-updated';

    useEffect(() => {
        setOpeningPaymentUpdate(false);
        setPaymentRetryStatus(null);
        setError(null);
        setDismissed(false);
        hasRetriedPayment.current = false;
        hasTrackedModalShown.current = false;
    }, [subscription?.past_due_at, user?.id]);

    const analyticsMetadata = useMemo(() => ({
        provider: 'stripe',
        payment_access_restricted: paymentAccessRestricted,
        subscription_id: subscription?.id,
        plan_id: subscription?.plan?.id,
    }), [paymentAccessRestricted, subscription?.id, subscription?.plan?.id]);

    const retryPayment = useCallback(async (source, metadata) => {
        setPaymentRetryStatus('retrying');
        setError(null);

        try {
            const data = await retryStripePastDuePayment();
            const status = data?.status || 'payment_failed';

            setPaymentRetryStatus(status);

            if (status === 'paid') {
                Analytics.sendCustomEvent(
                    source === 'manual'
                        ? 'past_due_payment_manual_retry_succeeded'
                        : 'past_due_payment_update_retry_succeeded',
                    { ...metadata, retry_source: source },
                );
            }
        } catch (requestError) {
            console.log('Could not retry the past-due payment', requestError);
            setPaymentRetryStatus('retry_error');
            setError('We could not retry your payment right now. Please try again.');
        }
    }, []);

    useEffect(() => {
        if (isAuthenticating || !hasUser || !paymentPastDue || hasTrackedModalShown.current) {
            return;
        }

        hasTrackedModalShown.current = true;
        Analytics.sendCustomEvent('past_due_payment_modal_shown', analyticsMetadata);
    }, [analyticsMetadata, hasUser, isAuthenticating, paymentPastDue]);

    useEffect(() => {
        if (!returnedFromPaymentMethodUpdate || !paymentPastDue || !isTeamOwner || hasRetriedPayment.current) {
            return;
        }

        hasRetriedPayment.current = true;
        let active = true;

        const retryPaymentAfterPaymentUpdate = async () => {
            Analytics.sendCustomEvent('past_due_payment_update_retrying_modal_shown', analyticsMetadata);

            await retryPayment('payment_method_update', analyticsMetadata);

            if (active) {
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.delete('billing');
                window.history.replaceState({}, document.title, `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
            }
        };

        retryPaymentAfterPaymentUpdate();

        return () => {
            active = false;
        };
    }, [analyticsMetadata, isTeamOwner, paymentPastDue, returnedFromPaymentMethodUpdate, retryPayment]);

    const handleDismiss = () => {
        setDismissed(true);
    };

    const handleUpdatePaymentMethod = async () => {
        if (openingPaymentUpdate) {
            return;
        }

        setOpeningPaymentUpdate(true);
        setError(null);
        Analytics.sendCustomEvent('past_due_payment_update_clicked', analyticsMetadata);

        try {
            const data = await createStripePaymentMethodUpdateLink();

            if (!data?.url) {
                throw new Error('Missing payment-method update URL.');
            }

            window.location.href = data.url;
        } catch (requestError) {
            console.log('Could not open the payment-method update page', requestError);
            setError('We could not open Stripe right now. Please try again.');
            setOpeningPaymentUpdate(false);
        }
    };

    const handleManualRetryPayment = async () => {
        if (paymentRetryStatus === 'retrying' || paymentRetryStatus === 'payment_pending') {
            return;
        }

        Analytics.sendCustomEvent('past_due_payment_manual_retry_clicked', analyticsMetadata);
        await retryPayment('manual', analyticsMetadata);
    };

    if (isAuthenticating || !user || !paymentPastDue || dismissed) {
        return null;
    }

    const graceDeadline = subscription?.grace_days_ended_at;
    const isRetryingPayment = paymentRetryStatus === 'retrying';
    const paymentWasRecovered = paymentRetryStatus === 'paid';
    const canRetryPayment = isTeamOwner
        && !paymentWasRecovered
        && !isRetryingPayment
        && paymentRetryStatus !== 'payment_pending';
    const planName = getPastDuePlanName(subscription?.plan);
    const title = paymentWasRecovered
        ? 'Payment received'
        : isRetryingPayment
            ? `Restoring your ${planName} access`
            : paymentAccessRestricted
                ? `Restore your ${planName} access`
                : `Keep your ${planName} access active`;
    const message = (
        <div className="past-due-payment-modal__content">
            {paymentWasRecovered ? (
                <p>Your payment was received and your {planName} access has been restored. Refresh this page to update your account.</p>
            ) : isRetryingPayment ? (
                <p>We are retrying your latest payment with Stripe now.</p>
            ) : paymentRetryStatus === 'payment_pending' ? (
                <p>Stripe is still processing your payment. Please wait a moment, then refresh this page.</p>
            ) : paymentRetryStatus === 'payment_failed' ? (
                <p>Stripe could not charge the updated payment method. Please try another payment method.</p>
            ) : paymentAccessRestricted ? (
                <p>
                    We could not process your latest payment. Premium downloads and Plugins are temporarily paused until your payment is resolved.
                </p>
            ) : (
                <p>
                    We could not process your latest payment. Your Pro access remains active until <strong>{getReadableDate(graceDeadline)}</strong>.
                </p>
            )}
            {isTeamOwner && !paymentWasRecovered && !isRetryingPayment ? (
                <p>Update your payment method in Stripe to restore and protect your {planName} access.</p>
            ) : !paymentWasRecovered && !isRetryingPayment ? (
                <p>Please ask your team owner to update the payment method in Stripe.</p>
            ) : null}
            {canRetryPayment && (
                <button
                    type="button"
                    className="past-due-payment-modal__retry-button"
                    onClick={handleManualRetryPayment}
                >
                    Retry payment
                </button>
            )}
            {error && <p className="past-due-payment-modal__error">{error}</p>}
        </div>
    );
    const footer = (
        <p className="past-due-payment-modal__support">
            Questions or issues? <a href="mailto:Support@ProductionCrate.com">Email Support@ProductionCrate.com</a>
        </p>
    );

    return (
        <Modal
            visible={true}
            pretitle={(
                <>
                    <i className="glyphicon glyphicon-lock past-due-payment-modal__lock" aria-hidden="true" />
                    <span>Payment action needed</span>
                </>
            )}
            title={title}
            msg={message}
            footer={footer}
            cta={isTeamOwner && !paymentWasRecovered && !isRetryingPayment ? (openingPaymentUpdate ? 'Opening Stripe...' : 'Update payment method') : false}
            ctaCallback={handleUpdatePaymentMethod}
            ctaDisabled={openingPaymentUpdate}
            ctaClose="Dismiss"
            ctaCloseCallback={handleDismiss}
            overlayCallback={handleDismiss}
            overlayClassName="past-due-payment-modal__overlay"
            showCloseIcon={true}
            className="past-due-payment-modal"
        />
    );
};

export default PastDuePaymentModal;
